import React, { Component } from 'react';
import { fetchFamilyTreeData } from '../../services/genealogyAPI';

/* global FamilyTree */

/**
 * FamilyTreeBalkan — Default Balkan FamilyTree JS with API integration.
 *
 * Uses the library's native UI for rendering, layout, zoom/pan, search.
 * Intercepts add actions to open the unified InviteFamilyModal (managed by parent).
 *
 * Props:
 *   onPersonClick(personId)     — navigate to /life/:personId
 *   onAddRelative({ personId, personName, relationship }) — open unified add modal
 *   activeFamilyId              — current family UUID
 */
class FamilyTreeBalkan extends Component {
  constructor(props) {
    super(props);
    this.divRef = React.createRef();
    this.family = null;
    this.state = {
      loading: true,
      error: null,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.loading !== nextState.loading ||
      this.state.error !== nextState.error
    );
  }

  async componentDidMount() {
    try {
      const treeData = await fetchFamilyTreeData();
      const nodes = this.transformNodes(treeData.nodes || []);
      this.initTree(nodes, treeData.root_person_id);
      this.setState({ loading: false });
    } catch (err) {
      console.error('Failed to load tree:', err);
      this.setState({ loading: false, error: 'Failed to load family tree' });
    }
  }

  transformNodes(apiNodes) {
    return apiNodes.map(node => ({
      id: node.id,
      mid: node.mid || undefined,
      fid: node.fid || undefined,
      pids: node.pids || [],
      name: node.name || '',
      gender: node.gender === 'unknown' ? '' : (node.gender || ''),
      born: node.born || '',
      img: node.photo || '',
    }));
  }

  initTree(nodes, rootPersonId) {
    // Store nodes for lookup
    this._nodes = nodes;

    // Find the topmost ancestor by walking up from the root person.
    // The root person (logged-in user) may have parents — we need the
    // highest ancestor reachable from them to show the full tree.
    const nodeMap = {};
    nodes.forEach(n => { nodeMap[n.id] = n; });

    let topAncestor = rootPersonId;
    if (topAncestor && nodeMap[topAncestor]) {
      const visited = new Set();
      let current = topAncestor;
      while (current && !visited.has(current)) {
        visited.add(current);
        const node = nodeMap[current];
        if (!node) break;
        // Walk to father first, then mother
        if (node.fid && nodeMap[node.fid]) {
          current = node.fid;
        } else if (node.mid && nodeMap[node.mid]) {
          current = node.mid;
        } else {
          break;
        }
      }
      topAncestor = current;
    }

    this.family = new FamilyTree(this.divRef.current, {
      mode: 'dark',
      roots: topAncestor ? [topAncestor] : undefined,
      nodeTreeMenu: true,
      scaleInitial: FamilyTree.match.boundary,
      toolbar: {
        zoom: true,
        fit: true,
        fullScreen: true,
      },
      nodeBinding: {
        field_0: 'name',
        field_1: 'born',
        img_0: 'img',
      },
      nodeMenu: {
        details: { text: 'Details' },
        edit: { text: 'Edit' },
        add: { text: 'Add' },
        remove: { text: 'Remove' },
      },
      editForm: {
        titleBinding: 'name',
        photoBinding: 'img',
        elements: [
          { type: 'textbox', label: 'Name', binding: 'name' },
          { type: 'textbox', label: 'Born', binding: 'born' },
        ],
        buttons: {
          edit: null,
          share: null,
          pdf: null,
        },
      },
      nodes: nodes,
    });

    // Guard against Balkan library crashes in _globalClickHandler.
    // The minified library accesses properties on DOM elements and node
    // data it assumes exist (.id, .type, ._ft_method). When the tree
    // has placeholder nodes (Add father, Add son, etc.) or stale SVG
    // references, these lookups crash on undefined.
    //
    // We cannot patch individual call sites in minified code. Wrap the
    // entire handler in a try-catch that swallows TypeErrors from
    // undefined property access. The tree remains functional — the
    // click on an unresolvable element is simply ignored.
    // Navigate to person's life page on node click
    this.family.onNodeClick((sender, args) => {
      if (!args || !args.node) return false;
      if (this.props.onPersonClick) {
        this.props.onPersonClick(args.node.id);
      }
      return false;
    });

    // Intercept add: cancel Balkan's default, open our modal instead
    this.family.onUpdateNode((args) => {
      console.log('onUpdateNode fired:', JSON.stringify(args, null, 2));
      if (args.addNodesData && args.addNodesData.length > 0) {
        const newNode = args.addNodesData[0];
        console.log('New node from Balkan:', newNode);
        console.log('Current _nodes count:', (this._nodes || []).length);
        const relationship = this._detectRelationship(newNode, args.updateNodesData);
        console.log('Detected relationship:', relationship);
        console.log('onAddRelative prop:', typeof this.props.onAddRelative);

        if (relationship && this.props.onAddRelative) {
          this.props.onAddRelative({
            personId: relationship.personId,
            personName: relationship.personName,
            relationship: relationship.type,
          });
        } else {
          console.warn('No relationship detected or no onAddRelative prop');
        }

        // Cancel Balkan's add — we handle it via modal + API
        return false;
      }
    });
  }

  /**
   * Determine relationship type and target person from Balkan's new node data.
   */
  _detectRelationship(newNode, updateNodesData) {
    let personId = null;
    let type = null;

    // Child added (has parent refs)
    if (newNode.mid || newNode.fid) {
      personId = newNode.mid || newNode.fid;
      type = newNode.gender === 'female' ? 'daughter' : 'son';
    }
    // Spouse added (has partner refs)
    else if (newNode.pids && newNode.pids.length > 0) {
      personId = newNode.pids[0];
      type = newNode.gender === 'female' ? 'wife' : 'husband';
    }

    // Parent added — check updateNodesData first (the Balkan library puts
    // the fid/mid update there, not on the new node itself), then fall
    // back to checking existing nodes
    if (!type) {
      const nodesToCheck = [
        ...(updateNodesData || []),
        ...(this._nodes || []),
      ];
      for (const existing of nodesToCheck) {
        if (existing.mid === newNode.id) {
          personId = existing.id;
          type = 'mother';
          break;
        }
        if (existing.fid === newNode.id) {
          personId = existing.id;
          type = 'father';
          break;
        }
      }
    }

    if (!personId || !type) return null;

    // Find person name for the modal
    const node = (this._nodes || []).find(n => n.id === personId);
    const personName = node ? node.name : '';

    return { personId, personName, type };
  }

  /**
   * Refresh tree data from API. Called by parent after modal submit.
   */
  async refreshTree() {
    try {
      const treeData = await fetchFamilyTreeData();
      const nodes = this.transformNodes(treeData.nodes || []);
      this._nodes = nodes;
      this.family.load(nodes);
    } catch (err) {
      console.error('Failed to refresh tree:', err);
    }
  }

  render() {
    const { loading, error } = this.state;

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)', zIndex: 10, color: 'white',
            fontSize: '18px',
          }}>
            Loading family tree...
          </div>
        )}

        {error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: '#1a1a1a', color: '#ff6b6b', fontSize: '16px',
          }}>
            {error}
          </div>
        )}

        <div id="tree" ref={this.divRef} style={{ width: '100%', height: '100%' }} />
      </div>
    );
  }
}

export default FamilyTreeBalkan;
